import { Component, type ReactNode } from "react";

import { EmptyState, PageFrame } from "@don/design-system";

import type { Reporter } from "./reporter.js";

/**
 * The Console's root error boundary (issue 243): a render crash anywhere below it is reported as
 * `console_render_crash` and the page draws one fixed sentence and a reload in place of the
 * tree that threw.
 *
 * The thrown value is never read: neither `getDerivedStateFromError` nor `componentDidCatch`
 * takes it, so its message, stack and component stack cannot reach the report or the page.
 */
export class ErrorBoundary extends Component<
  { reporter: Pick<Reporter, "report">; children?: ReactNode },
  { crashed: boolean }
> {
  override state = { crashed: false };

  static getDerivedStateFromError(): { crashed: boolean } {
    return { crashed: true };
  }

  override componentDidCatch(): void {
    this.props.reporter.report("console_render_crash");
  }

  override render(): ReactNode {
    if (!this.state.crashed) return this.props.children;
    return (
      <div data-console-crashed style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
        <PageFrame>
          <EmptyState
            flush
            title="Something went wrong"
            body="This page stopped working. Reload to continue."
            primary={{ children: "Reload", onClick: () => window.location.reload() }}
          />
        </PageFrame>
      </div>
    );
  }
}
